import * as z from 'zod';

import { RadioButtonOptionsProps } from 'components/RadioButton/RadioButton';

import { getFonts } from '../../components/Theme/utils';

import { SettingsFormData } from './types';

export const DEFAULT_COLOR = '#CB1010';
export const DEFAULT_FONT_SIZE = 12;

export const loginPopupFontWeightOptions = [
  {
    value: 'regular',
    label: 'Regular'
  },
  {
    value: 'medium',
    label: 'Medium'
  },
  {
    value: 'bold',
    label: 'Bold'
  }
];

export enum EDesktopNavigationLayout {
  LEFT_SIDEBAR = 'leftSidebar',
  TOP_FIXED_HEADER = 'topFixedHeader'
}

export const MENU_RADIO_BUTTON_OPTIONS: RadioButtonOptionsProps[] = [
  {
    value: EDesktopNavigationLayout.LEFT_SIDEBAR,
    id: EDesktopNavigationLayout.LEFT_SIDEBAR,
    label: 'Left Sidebar'
  },
  {
    value: EDesktopNavigationLayout.TOP_FIXED_HEADER,
    id: EDesktopNavigationLayout.TOP_FIXED_HEADER,
    label: 'Top Fixed Header'
  }
];

const getInputFontOptions = () =>
  getFonts().map(({ content }) => ({
    value: content,
    label: content
  }));

export const inputFontOptions = getInputFontOptions();

export const SETTINGS_FORM_SCHEMA = z.object({
  generalBackgroundColor: z.string().nonempty('This field is required.'),
  generalSectionTextColor: z.string().nonempty('This field is required.'),
  generalFavicon: z.string().nonempty('This field is required.'),
  headerTitle: z.string().optional(),
  headerLogo: z.string().optional(),
  headerBackgroundColor: z.string().nonempty('This field is required.'),
  headerTextColor: z.string().nonempty('This field is required.'),
  headerIsPlayerNameShown: z.boolean().optional(),
  headerIsPlayerIdShown: z.boolean().optional(),
  headerIsPlayerBalanceShown: z.boolean().optional(),
  headerIsLoginBtnShown: z.boolean().optional(),
  headerIsLanguageSwitcherShown: z.boolean().optional(),
  headerLoginButton: z.object({
    backgroundColor: z.string().optional(),
    text: z.string().optional()
  }),
  settings: z.object({
    seo: z.object({
      portalTitle: z.string().optional(),
      portalDescription: z.string().nonempty('This field is required.'),
      portalOGImage: z.string().optional(),
      portalKeywords: z.array(z.string()).optional()
    }),
    addToHomeScreen: z
      .object({
        active: z.boolean().optional(),
        borderColor: z.string().optional(),
        backgroundColor: z.string().optional(),
        buttonImage: z.string().optional(),
        iconImage: z.string().optional(),
        shortcutName: z.string().optional()
      })
      .refine(
        (data) => {
          // If active is true, both iconImage and shortcutName are required
          if (data.active) {
            return (
              data.iconImage &&
              data.iconImage.trim() !== '' &&
              data.shortcutName &&
              data.shortcutName.trim() !== ''
            );
          }
          // If active is false, these fields are not required
          return true;
        },
        {
          message:
            'Icon Image and Store Title are required when the feature is activated',
          path: ['iconImage']
        }
      )
      .refine(
        (data) => {
          // If active is true, shortcutName is required
          if (data.active) {
            return data.shortcutName && data.shortcutName.trim() !== '';
          }
          // If active is false, this field is not required
          return true;
        },
        {
          message: 'Store Title is required when the feature is activated',
          path: ['shortcutName']
        }
      )
  }),
  menuBackgroundColor: z.string().nonempty('This field is required.'),
  menuTextColor: z.string().nonempty('This field is required.'),
  menuHoverColor: z.string().nonempty('This field is required.'),
  menuDesktopNavigationLayout: z.nativeEnum(EDesktopNavigationLayout),
  contentCardBackgroundColor: z.string().optional(),
  contentCardTextColor: z.string().optional(),
  contentCardButtonColor: z.string().optional(),
  contentPageTextColor: z.string().optional(),
  footerBackgroundColor: z.string().nonempty('This field is required.'),
  selectedTabColor: z.string().nonempty('This field is required.'),
  footerTextColor: z.string().nonempty('This field is required.'),
  footerPrivacyLink: z.string().nonempty('This field is required.'),
  footerTermsConditionsLink: z.string().nonempty('This field is required.'),
  footerSupportLink: z.string().nonempty('This field is required.'),
  loginPopupText: z.string().optional(),
  loginPopupFont: z.string().nonempty('This field is required.'),
  loginPopupFontSize: z
    .number()
    .min(DEFAULT_FONT_SIZE, 'Font size must be at least 12px'),
  loginPopupFontColor: z.string().nonempty('This field is required.'),
  loginPopupFontWeight: z.string().nonempty('This field is required.'),
  loginPopupBackgroundImageMobile: z
    .string()
    .nonempty('This field is required.'),
  loginPopupBackgroundImageDesktop: z
    .string()
    .nonempty('This field is required.'),
  loginPopupLogo: z.string().nonempty('This field is required.'),
  loginPopupLogoSize: z.number().optional(),
  loginPopupConsentSection: z
    .object({
      htmlText: z.string().optional(),
      consentRequired: z.boolean().optional()
    })
    .optional(),
  loginPopupOTPButtonIcon: z.string(),
  loginPopupOTPButtonText: z.string(),
  loginPopupOTPButtonColor: z.string(),
  loginPopupOTPButtonTextColor: z.string()
});

export const buildParsedSettingsData = (data: SettingsFormData) => {
  const withColor = (color: string) => ({ colorOne: color });

  return {
    settings: {
      seo: {
        portalDescription: data.settings.seo.portalDescription,
        portalKeywords: data.settings.seo.portalKeywords,
        portalTitle: data.settings.seo.portalTitle,
        portalOGImage: data.settings.seo.portalOGImage || null
      },
      addToHomeScreen: {
        active: data.settings.addToHomeScreen.active,
        borderColor: data.settings.addToHomeScreen.borderColor
          ? { colorOne: data.settings.addToHomeScreen.borderColor }
          : undefined,
        backgroundColor: data.settings.addToHomeScreen.backgroundColor
          ? { colorOne: data.settings.addToHomeScreen.backgroundColor }
          : undefined,
        buttonImage: data.settings.addToHomeScreen.buttonImage || null,
        iconImage: data.settings.addToHomeScreen.iconImage,
        shortcutName: data.settings.addToHomeScreen.shortcutName
      }
    },
    theme: {
      general: {
        backgroundColor: withColor(data.generalBackgroundColor),
        sectionTextColor: data.generalSectionTextColor,
        portalFavIcon: data.generalFavicon || null
      },
      header: {
        backgroundColor: withColor(data.headerBackgroundColor),
        textColor: data.headerTextColor,
        loginButton: {
          backgroundColor: withColor(
            data.headerLoginButton.backgroundColor as string
          )
        },
        title: data.headerTitle,
        logo: data.headerLogo || null,
        showPlayerName: data.headerIsPlayerNameShown,
        showPlayerId: data.headerIsPlayerIdShown,
        showPlayerBalance: data.headerIsPlayerBalanceShown,
        showLogin: data.headerIsLoginBtnShown,
        showTranslations: data.headerIsLanguageSwitcherShown
      },
      menu: {
        backgroundColor: withColor(data.menuBackgroundColor),
        textColor: data.menuTextColor || DEFAULT_COLOR,
        tabHoverColor: withColor(data.menuHoverColor),
        selectedTabColor: data.selectedTabColor,
        desktopNavigationLayout:
          data.menuDesktopNavigationLayout ||
          EDesktopNavigationLayout.LEFT_SIDEBAR
      },
      contentCard: {
        backgroundColor: withColor(data.contentCardBackgroundColor as string),
        textColor: data.contentCardTextColor || DEFAULT_COLOR,
        buttonColor: withColor(data.contentCardButtonColor as string)
      },
      contentPage: {
        textColor: data.contentPageTextColor || DEFAULT_COLOR
      },
      footer: {
        backgroundColor: withColor(data.footerBackgroundColor),
        textColor: data.footerTextColor,
        privacyPolicyUrl: data.footerPrivacyLink,
        termsOfServiceUrl: data.footerTermsConditionsLink,
        supportUrl: data.footerSupportLink
      },
      loginPopup: {
        text: data.loginPopupText,
        font: data.loginPopupFont,
        fontSize: data.loginPopupFontSize,
        fontColor: data.loginPopupFontColor,
        fontWeight: data.loginPopupFontWeight,
        backgroundImageMobile: data.loginPopupBackgroundImageMobile || null,
        backgroundImageDesktop: data.loginPopupBackgroundImageDesktop || null,
        logo: data.loginPopupLogo,
        logoSize: data.loginPopupLogoSize,
        consentSection: {
          htmlText: data.loginPopupConsentSection?.htmlText,
          consentRequired: data.loginPopupConsentSection?.consentRequired
        },
        otpButton: {
          icon: data?.loginPopupOTPButtonIcon || null,
          text: data?.loginPopupOTPButtonText || '',
          backgroundColor: withColor(data?.loginPopupOTPButtonColor as string),
          textColor: withColor(data?.loginPopupOTPButtonTextColor as string)
        }
      }
    }
  };
};
